import { Alert, Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import HiddenFileInput from "@entrilia/shared/components/inputs/HiddenFileInput";
import LoaderBox from "@entrilia/shared/components/LoaderBox";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import { logError } from "@entrilia/shared/logging";
import { readFileAsDataUrl } from "@entrilia/shared/utilities/fileHelper";
import { api } from "../../../../api/client";
import useClientContext from "../../../../contexts/useClientContext";
import useClientPageTitle from "../../../../contexts/useClientPageTitle";
import { UserActions } from "../../../../store/state/user/actions";
import {
  identityAvatarImageSelector,
  identityLargeImageSelector,
  impersonationSelector,
  loadingIdentitySelector,
} from "../../../../store/state/user/selectors";
import { loadLargeAvatar } from "../../../../store/state/user/thunks";
import type { AppDispatch } from "../../../../store/store";

const maxAvatarSizeBytes = 1 * 1024 * 1024;
const avatarFileExtensions = [".png", ".jpg", ".jpeg"];

const updateIdentity = withErrorHandling(api.user.updateIdentity);
const processAvatar = withErrorHandling(api.user.processAvatar);

const MyProfile = () => {
  useClientPageTitle("My Profile");

  const { contactName, contactEmail } = useClientContext();

  const dispatch: AppDispatch = useDispatch();

  const [saving, setSaving] = useState(false);
  const [avatarAlert, setAvatarAlert] = useState("");
  const [avatarProcessing, setAvatarProcessing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const identityAvatarImage = useSelector(identityAvatarImageSelector);
  const identityLargeImage = useSelector(identityLargeImageSelector);
  const isImpersonation = useSelector(impersonationSelector);

  const [avatarImage, setAvatarImage] = useState(identityAvatarImage);
  const [prevIdentityAvatarImage, setPrevIdentityAvatarImage] = useState(identityAvatarImage);
  if (identityAvatarImage !== prevIdentityAvatarImage) {
    setPrevIdentityAvatarImage(identityAvatarImage);
    setAvatarImage(identityAvatarImage);
  }

  useEffect(() => {
    if (identityLargeImage === false) {
      dispatch(loadLargeAvatar());
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [identityLargeImage]);

  const handleSaveChanges = async () => {
    setSaving(true);

    const [identity, error] = await updateIdentity({ avatarImage });

    setSaving(false);

    if (error) {
      logError(error, "[MyProfile] handleSaveChanges");
      setAvatarAlert("We weren't able to save your changes. Could you please try again in a few minutes?");
      return;
    }

    dispatch(UserActions.UpdateIdentity(identity));
    setIsDirty(false);
  };

  const handleRemoveAvatarImage = () => {
    setAvatarImage("");
    setIsDirty(true);
  };

  const handleAvatarFilesAdd = async (files: File[]) => {
    const file = files[0];
    if (file === undefined) {
      return;
    }

    if (file.size > maxAvatarSizeBytes) {
      setAvatarAlert("The file is too large. Please load an image smaller than 1 MB");
      return;
    }

    setAvatarAlert("");
    setAvatarProcessing(true);

    try {
      const fileDataUrl = await readFileAsDataUrl(file);

      const [image, error] = await processAvatar(fileDataUrl);
      setAvatarProcessing(false);
      if (error) {
        logError(error, "[MyProfile] processAvatar");
        setAvatarAlert(`We weren't able to save your changes. ${error.message}`);
        return;
      }

      setAvatarImage(image);
      setIsDirty(true);
    } catch {
      setAvatarAlert(
        "We weren't able to save your changes. Could you please try refreshing the page and uploading again?"
      );
      setAvatarProcessing(false);
      return;
    }
  };

  return (
    <LoaderBox loading={useSelector(loadingIdentitySelector)}>
      <Stack spacing={3} sx={{ width: "100%", py: 3, px: 4 }}>
        <Typography variant="h5">My Profile</Typography>
        {!!avatarAlert && (
          <Alert severity="error" onClose={() => setAvatarAlert("")}>
            {avatarAlert}
          </Alert>
        )}
        <Stack direction="row" spacing={8} sx={{ paddingTop: "1rem" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle1">My Photo</Typography>
            <Box sx={{ my: 2 }}>
              <UserAvatar size={100} userName={contactName} avatarImage={avatarImage} />
            </Box>
            <LinearProgress sx={{ marginBottom: "3px", visibility: avatarProcessing ? "visible" : "hidden" }} />
            <Button
              variant="outlined"
              component="label"
              color="secondary"
              disabled={avatarProcessing || isImpersonation}
              sx={{ whiteSpace: "nowrap" }}
            >
              Upload Photo
              <HiddenFileInput acceptedFileTypes={avatarFileExtensions} onFilesAdd={handleAvatarFilesAdd} />
            </Button>

            {avatarImage ? (
              <Button
                variant="text"
                color="secondary"
                sx={{ whiteSpace: "nowrap" }}
                onClick={handleRemoveAvatarImage}
                disabled={avatarProcessing || isImpersonation}
              >
                Remove
              </Button>
            ) : (
              <Typography noWrap variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                Max. file size: 1 MB
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1">Personal Information</Typography>
            <Stack spacing={2} sx={{ pt: 2 }}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                <Typography noWrap color="textSecondary" sx={{ width: "7rem" }}>
                  Full Name
                </Typography>
                <Typography noWrap>{contactName}</Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                <Typography noWrap color="textSecondary" sx={{ width: "7rem" }}>
                  Email Address
                </Typography>
                <Typography noWrap>{contactEmail}</Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ boxShadow: "unset", width: "7rem" }}
          disabled={isImpersonation || !isDirty}
          loading={saving}
          onClick={handleSaveChanges}
        >
          Save Changes
        </Button>
      </Stack>
    </LoaderBox>
  );
};

export default MyProfile;
